import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/sports';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';

const categoryColors: Record<string, string> = {
  football: 'bg-primary-500',
  basketball: 'bg-accent-500',
  tennis: 'bg-secondary-500',
  esports: 'bg-primary-600',
  baseball: 'bg-accent-600',
};

const categoryIconMap: Record<string, string> = {
  football: 'ri-football-line',
  basketball: 'ri-basketball-line',
  tennis: 'ri-trophy-line',
  esports: 'ri-gamepad-line',
  baseball: 'ri-riding-line',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-foreground-400 text-2xl"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-950 mb-2">分类未找到</h2>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const breadcrumbSchema = {
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.bbsports.com/' },
      { '@type': 'ListItem', position: 2, name: `${category.name}赛事`, item: `https://www.bbsports.com/category/${category.slug}` },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />

      <header>
        <Navbar />
      </header>

      <main>
        <section className="relative w-full pt-24 pb-10 md:pb-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <span className="text-foreground-300">/</span>
              <span className="text-foreground-700 font-medium">{category.name}赛事</span>
            </nav>

            <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6 mb-8">
              <div className={`w-14 h-14 md:w-16 md:h-16 rounded-xl ${categoryColors[category.slug] || 'bg-primary-500'} flex items-center justify-center`}>
                <i className={`${categoryIconMap[category.slug] || 'ri-football-line'} text-white text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-1">
                  {category.name}赛事
                </h1>
                <p className="text-sm md:text-base text-foreground-600 max-w-xl">
                  {category.description}
                </p>
              </div>
              <div className="md:ml-auto flex items-center gap-2 px-4 py-2 rounded-lg bg-background-50 border border-background-200/70">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-fire-line text-primary-500 text-sm"></i>
                </div>
                <span className="text-sm font-label font-semibold text-foreground-700">
                  当前 {category.matchCount} 场赛事
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-6 md:py-8 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 mb-6 flex-wrap">
              <span className="text-xs font-label font-semibold text-foreground-500 uppercase tracking-wider">赛事分类：</span>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-label font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {category.events.map((event) => (
                <div
                  key={event.id}
                  className="bg-background-50 border border-background-200/70 rounded-xl p-5 hover:border-primary-300 transition-all duration-300"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-700 text-xs font-label font-medium">
                      {event.league}
                    </span>
                    <div className="flex items-center gap-1.5 text-xs text-foreground-500">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-time-line text-xs"></i>
                      </div>
                      <span>{event.time}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4 p-3 bg-background-100/70 rounded-lg">
                    <div className="text-center flex-1">
                      <p className="text-sm font-heading font-semibold text-foreground-950">{event.home}</p>
                    </div>
                    <span className="text-xs font-heading font-bold text-foreground-400 px-3">VS</span>
                    <div className="text-center flex-1">
                      <p className="text-sm font-heading font-semibold text-foreground-950">{event.away}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {event.drawOdds ? (
                      <>
                        <div className="flex-1 text-center py-2 rounded-lg bg-background-100 hover:bg-primary-100/50 transition-colors cursor-pointer">
                          <p className="text-xs text-foreground-500 mb-0.5">主胜</p>
                          <p className="text-sm font-label font-bold text-primary-500">{event.homeOdds}</p>
                        </div>
                        <div className="flex-1 text-center py-2 rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer">
                          <p className="text-xs text-foreground-500 mb-0.5">平局</p>
                          <p className="text-sm font-label font-bold text-foreground-700">{event.drawOdds}</p>
                        </div>
                        <div className="flex-1 text-center py-2 rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer">
                          <p className="text-xs text-foreground-500 mb-0.5">客胜</p>
                          <p className="text-sm font-label font-bold text-foreground-700">{event.awayOdds}</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex-1 text-center py-2 rounded-lg bg-background-100 hover:bg-primary-100/50 transition-colors cursor-pointer">
                          <p className="text-xs text-foreground-500 mb-0.5">主胜</p>
                          <p className="text-sm font-label font-bold text-primary-500">{event.homeOdds}</p>
                        </div>
                        <div className="flex-1 text-center py-2 rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer">
                          <p className="text-xs text-foreground-500 mb-0.5">客胜</p>
                          <p className="text-sm font-label font-bold text-foreground-700">{event.awayOdds}</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto text-center">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">
              更多{category.name}精彩内容
            </h2>
            <p className="text-sm text-foreground-600 mb-6">
              查看{category.name}相关的最新资讯和专业分析
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-label font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer"
            >
              返回首页查看更多
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </div>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}